<script setup lang="ts">
import MapItem from '~/components/map.vue'

import type { TripData } from '~/types/trip'

const props = defineProps<{
  trip: TripData
  polylineCoordinates: Array<[number, number]>
}>()

const emit = defineEmits<{
  (e: 'created'): void
}>()

const authStore = useAuthStore()
const router = useRouter()
const { t } = useI18n()
const { styleUrl } = useMapStyles()

const showRewindModal = ref(false)

const rewindMapStyle = styleUrl('RISER-Rewind')

const isUserPro = computed(() => authStore.isUserPro)

const handleClick = () => {
  if (props.trip.rewind_render_status === 'CREATED' && props.trip.rewind_video_url) {
    router.push(`/trips/${props.trip.id}/rewind-video`)
    return
  }

  if (props.trip.rewind_render_status === 'NONE') {
    openRewindModal()
  }
}

const openRewindModal = () => {
  const { requirePro } = useProGate()

  requirePro('rewind', () => {
    showRewindModal.value = true
  })
}

const onCreated = () => {
  showRewindModal.value = false
  emit('created')
}
</script>

<template>
  <div
    class="tw-group tw-relative tw-flex tw-w-full tw-flex-col tw-overflow-hidden tw-rounded-lg"
    :class="{ 'tw-cursor-pointer': trip.rewind_render_status !== 'PENDING' }"
    @click="handleClick"
  >
    <MapItem
      class="tw-absolute tw-inset-0 tw-h-full tw-w-full"
      :show-scale="false"
      :map-style="rewindMapStyle"
      :options="{
        zoom: 13,
        center: {
          lng: polylineCoordinates[0][0],
          lat: polylineCoordinates[0][1]
        },
        interactive: false,
        attributionControl: false
      }"
    >
      <template #default="{ map }">
        <MapRoute :coordinates="polylineCoordinates" :map="map" :line-width="4" />
      </template>
    </MapItem>

    <div
      class="tw-relative tw-flex tw-flex-1 tw-flex-col tw-items-center tw-gap-2 tw-bg-black/40 tw-px-4 tw-py-8 tw-text-center tw-transition-colors tw-duration-200 group-hover:tw-bg-black/55"
    >
      <IconsProBadge v-if="!isUserPro" class="tw-absolute tw-right-2 tw-top-2 tw-w-8" />

      <template v-if="trip.rewind_render_status === 'NONE'">
        <FontAwesomeIcon icon="video" class="tw-text-3xl tw-text-white tw-drop-shadow-md" />
        <span class="tw-text-lg tw-font-bold tw-text-white tw-drop-shadow-md">
          {{ t('rewind_trip_detail_title_none') }}
        </span>
        <span class="tw-text-sm tw-text-white/80">
          {{ t('rewind_trip_detail_description_none') }}
        </span>
        <span
          class="tw-mt-auto tw-rounded-full tw-bg-riser-blue tw-px-6 tw-py-2 tw-text-sm tw-font-bold tw-text-white"
        >
          {{ t('rewind_trip_detail_cta_none') }}
        </span>
      </template>

      <template v-else-if="trip.rewind_render_status === 'PENDING'">
        <FontAwesomeIcon
          icon="rotate-right"
          class="tw-animate-spin tw-text-3xl tw-text-white tw-drop-shadow-md"
        />
        <span class="tw-text-lg tw-font-bold tw-text-white tw-drop-shadow-md">
          {{ t('rewind_trip_detail_title_pending') }}
        </span>
        <span class="tw-text-sm tw-text-white/80">
          {{ t('rewind_trip_detail_description_pending') }}
        </span>
        <span class="tw-text-xs tw-text-white/60">
          {{ t('rewind_trip_detail_notification_hint') }}
        </span>
      </template>

      <template v-else>
        <FontAwesomeIcon
          icon="play"
          class="tw-text-3xl tw-text-white tw-drop-shadow-md tw-transition-transform tw-duration-200 group-hover:tw-scale-110"
        />
        <span class="tw-text-lg tw-font-bold tw-text-white tw-drop-shadow-md">
          {{ t('rewind_trip_detail_title_created') }}
        </span>
        <span class="tw-text-sm tw-text-white/80">
          {{ t('rewind_trip_detail_description_created') }}
        </span>
        <span
          class="tw-mt-auto tw-rounded-full tw-bg-riser-blue tw-px-6 tw-py-2 tw-text-sm tw-font-bold tw-text-white"
        >
          {{ t('rewind_trip_detail_cta_created') }}
        </span>
      </template>
    </div>

    <RewindCreateVideoModal
      v-if="showRewindModal"
      :trip-id="trip.id"
      :trip-title="trip.title"
      @close="showRewindModal = false"
      @created="onCreated"
    />
  </div>
</template>
